import _ from 'lodash';
import Vue from 'vue';

/**
 * Extend lodash.js
 */
_.mixin({
  flattenObject: function (object) {
    const toReturn = {};

    for (const i in object) {
      if (!Object.getOwnPropertyDescriptor(object, i)) {
        continue;
      }

      if (typeof object[i] == 'object' && object[i] !== null) {
        const flatObject = this.flattenObject(object[i]);
        for (const x in flatObject) {
          if (!Object.getOwnPropertyDescriptor(flatObject, x)) {
            continue;
          }

          toReturn[i + '.' + x] = flatObject[x];
        }
      } else {
        toReturn[i] = object[i];
      }
    }
    return toReturn;
  },

  /**
   * Ensure options are protected from modification
   * @param options
   * @param defaults
   * @return {ReadonlyArray<any>}
   */
  options: function (options, defaults) {
    if (!options || typeof options !== 'object') {
      options = {};
    }

    return Object.freeze(
      this.defaultsDeep(this.compactObject(options), defaults)
    );
  },

  /**
   * Constrain value between min and max limits
   * @param min
   * @param value
   * @param max
   * @return {number}
   */
  constrain: function (min, value, max) {
    value = Math.max(value, min);
    value = Math.min(value, max);
    return value;
  },

  /**
   * Test that float value resides between min and max
   * @param min {Number}
   * @param value {Number}
   * @param max {Number}
   * @param [o] {{includeMin, includeMax}}}
   * @return {boolean}
   */
  between: function (min, value, max, o) {
    if (
      !this.isDefined(min) ||
      !this.isDefined(value) ||
      !this.isDefined(max)
    ) {
      return false;
    }

    o = Object.assign(
      {
        includeMin: true,
        includeMax: true,
      },
      o
    );

    return (
      (o.includeMin ? min <= value : min < value) &&
      (o.includeMax ? value <= max : value < max)
    );
  },

  /**
   * Compare two objects. Depth level 1. Order of arguments is not relevant.
   * @example
   * JSON.stringify(_.diffObject({a:1, c:9, d:6}, {a:2, b:3, d:6}))
   * {"a":[1,2],"c":[9,null],"b":[null,3]}
   * @param a
   * @param b
   * @return {object}, object filled with non equal properties names
   * with value containing array of their values or empty object if `a` equal to `b`
   */
  diffObject: function (a, b) {
    a = a || {}; // weak defence
    b = b || {};
    const rv = {};
    _.each(a, function (value, key) {
      if (!_.isEqual(value, b[key])) {
        rv[key] = [value, b[key]];
      }
    });
    _.each(b, function (value, key) {
      if (!rv[key] && !_.isEqual(value, a[key])) {
        rv[key] = [a[key], value];
      }
    });
    return rv;
  },

  shallowDiff: function (a, b) {
    return _.omitBy(a, function (v, k) {
      return b[k] === v;
    });
  },

  /**
   * Convert native sync try{f1}catch(){f2} to async _.try(f1)[[.catch(f2)].then(f3)]
   * Surround given function by try/catch, returning promise for distinguishing between success/failure
   * @param f {Function}
   * @return {Deferred}
   */
  try: function (f) {
    const promise = new _.ColdPromise();
    window.setTimeout(function () {
      try {
        promise.resolve(f());
      } catch (bug) {
        console.trace('try', bug);
        promise.reject(bug);
      }
    }, 1);
    return promise;
  },

  handleException: function (fn, handler) {
    return function (...args) {
      try {
        typeof fn === 'function' && fn.call(this, ...args);
      } catch (xcp) {
        typeof handler === 'function' && handler.call(this, xcp);
      }
    };
  },

  cloneDeepAndClean: function (o) {
    return this.cloneDeepWith(o, (value) => {
      if (
        value instanceof Element ||
        value instanceof jQuery ||
        value instanceof Vue ||
        value instanceof Backbone.View ||
        value instanceof Backbone.Collection ||
        value instanceof Backbone.Model ||
        value === window ||
        value === document
      ) {
        return null;
      }
    });
  },

  /**
   * Compact object recursively handling self-recurrence
   * @test _.compactObject([10, null, void(0), 20]);
   * @test _.compactObject({a:1, b:null, c:void(0), d:{a:'a', b:[10,null],c:null}});
   * @param o
   * @return {*}
   */
  compactObject: function (o) {
    /* jshint validthis: true */
    let usedValues = new WeakSet();

    function __compactObject__(o) {
      let rv;

      if (o instanceof Object) {
        if (
          usedValues.has(o) ||
          o instanceof Element ||
          o instanceof jQuery ||
          o instanceof Vue ||
          o instanceof Backbone.View ||
          o instanceof Backbone.Collection ||
          o instanceof Backbone.Model ||
          o === window ||
          o === document
        ) {
          rv = o;
          return rv;
        } else {
          usedValues.add(o);
        }
      }

      if (o instanceof Array) {
        rv = [];
        this.each(o, (value) => {
          if (this.isDefined(value)) {
            if (value instanceof Object) {
              value = __compactObject__.call(this, value);
            }
            rv.push(value);
          }
        });
      } else if (o instanceof Object) {
        rv = {};
        this.each(o, (value, key) => {
          if (this.isDefined(value)) {
            if (value instanceof Object) {
              value = __compactObject__.call(this, value);
            }
            rv[key] = value;
          }
        });
      } else {
        rv = o;
      }

      return rv;
    }

    const rv = __compactObject__.call(this, o);
    usedValues = null;
    return rv;
  },

  isDefined: function (o) {
    return void 0 !== o && null !== o;
  },

  isNumeric: function (num) {
    return !isNaN(parseFloat(num));
  },

  readonly: function (o) {
    const rv = {};
    for (const p in o) {
      if (Object.getOwnPropertyDescriptor(o, p)) {
        Object.defineProperty(rv, p, {
          value: o[p],
          writable: false,
        });
      }
    }
    return rv;
  },

  /**
   *
   * @param o - Object to query
   * @param path - path
   *
   * Support a very basic object querying.
   * No arrays are supported.
   */
  objectPath: function (o, path) {
    if (!_.isString(path)) {
      return;
    }
    let currentNode = o;

    const pathSegments = path.split('.');

    for (const segment of pathSegments) {
      if (!currentNode) {
        break;
      }
      currentNode = currentNode[segment];
    }

    return currentNode;
  },

  isNotEmpty: function (o) {
    return !_.isEmpty(o);
  },

  /**
   * Wrapper for native promise exposing public resolve/reject methods
   * and status property.
   * Cancelable
   * Cold as opposed to hot where resolving is done in the promise belly :)
   */
  ColdPromise: class ColdPromise {
    constructor(fn) {
      this.promise = new Promise((resolve, reject) => {
        this.status = 'pending';

        this.resolve = this._whatif(resolve);
        this.reject = this._whatif(reject);
        fn && fn(this.resolve, this.reject);
      });

      this.promise.then(
        () => {
          this.status = 'resolved';
        },
        () => {
          this.status = 'rejected';
        }
      );
    }

    isPending() {
      return this.status === 'pending';
    }

    isCanceled() {
      return this.status === 'canceled';
    }

    cancel() {
      this.status = 'canceled';
      this._onCanceled && this._onCanceled();
    }

    _whatif(callback) {
      return (...args) => {
        return this.isCanceled() ? undefined : callback && callback(...args);
      };
    }

    /**
     * @param onResolve {Function}
     * @param [onReject] {Function}
     * @return {Promise}
     */
    then(onResolve, onReject) {
      return this.promise.then(
        this._whatif(onResolve),
        onReject && this._whatif(onReject)
      );
    }

    /**
     * @param onReject {Function}
     * @return {Promise}
     */
    catch(onReject) {
      return this.promise.catch(this._whatif(onReject));
    }

    /**
     * @param onFinally {Function}
     * @return {Promise}
     */
    finally(onFinally) {
      const callback = this._whatif(onFinally);
      return this.promise.then(callback, callback);
    }

    /**
     * Callback to be executed when promise being canceled
     * @param onCanceled
     */
    canceled(onCanceled) {
      this._onCanceled = onCanceled;
    }
  },

  /**
   * recursive deep loop
   * @param obj {object|array}
   * @param path {string}
   * @param ƒ {function}
   */
  eachIn(obj, path, ƒ) {
    const aPath = path.split('.');
    const prop = aPath.shift();

    if (aPath.length === 0) {
      if (!this.isDefined(obj)) {
        // exit recursion
      } else if (obj instanceof Array) {
        this.each(obj, (v) => {
          if (!this.isDefined(v)) {
            // continue
          } else if (v[prop] instanceof Array) {
            this.each(v[prop], (vv) => {
              ƒ(vv, v[prop]);
            });
          } else {
            ƒ(v[prop], v);
          }
        });
      } else if (obj[prop] instanceof Array) {
        this.each(obj[prop], (v) => {
          ƒ(v, obj[prop]);
        });
      } else {
        ƒ(obj[prop], obj);
      }
    } else {
      if (!this.isDefined(obj)) {
        // exit recursion
      } else if (obj instanceof Array) {
        this.each(obj, (v) => {
          this.eachIn(v, path, ƒ); // recursion
        });
      } else {
        this.eachIn(obj[prop], aPath.join('.'), ƒ); // recursion
      }
    }
  },

  /**
   * find index in array where item comparator evaluates to 0
   * uses binary search
   * assume sorted by condition
   * assume each item conditionally unique
   * @param arr {array}
   * @param comparator {function}
   * @returns {null|number} returns null if not found
   */
  findIndexInSorted(arr, comparator) {
    let l = 0;
    let h = arr.length;

    while (l < h) {
      const m = Math.trunc((l + h) / 2);
      const cmp = comparator(arr[m]);
      let setL = false;

      if (cmp === 0) {
        return m;
      } else {
        setL = cmp > 0;
      }

      if (setL) {
        l = m + 1;
      } else {
        h = m;
      }
    }

    return null;
  },
});

export default _;
